import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #333;
  font-size: 1.5rem;
`;

const Section = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  color: #333;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #dee2e6;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background-color: white;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const TimeInputGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-end;
`;

const TimeInputWrapper = styled.div`
  flex: 1;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.variant === 'primary' && `
    background-color: #007bff;
    color: white;

    &:hover {
      background-color: #0069d9;
    }

    &:disabled {
      background-color: #6c757d;
      cursor: not-allowed;
    }
  `}

  ${props => props.variant === 'secondary' && `
    background-color: #6c757d;
    color: white;

    &:hover {
      background-color: #5a6268;
    }
  `}

  ${props => props.variant === 'info' && `
    background-color: #17a2b8;
    color: white;

    &:hover {
      background-color: #138496;
    }
  `}
`;

const Alert = styled.div`
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  border: 1px solid ${props => props.type === 'success' ? '#c3e6cb' : props.type === 'error' ? '#f5c6cb' : '#cce7ff'};
  background-color: ${props => props.type === 'success' ? '#d4edda' : props.type === 'error' ? '#f8d7da' : '#e3f2fd'};
  color: ${props => props.type === 'success' ? '#155724' : props.type === 'error' ? '#721c24' : '#0d47a1'};
`;

const ConfigCard = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 1.5rem;
  border-radius: 8px;
  color: white;
  margin-bottom: 1.5rem;
`;

const ConfigCardTitle = styled.div`
  font-size: 0.875rem;
  opacity: 0.9;
  margin-bottom: 0.5rem;
`;

const ConfigCardValue = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${props => props.active ? '#d4edda' : '#f8d7da'};
  color: ${props => props.active ? '#155724' : '#721c24'};
`;




const QuickReplyCard = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
`;

const QuickReplyInput = styled.input`
  flex: 1;
  min-width: 150px;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const QuickReplySelect = styled.select`
  width: 140px;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  background: white;
`;

const DeleteButton = styled.button`
  padding: 0.5rem 0.75rem;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background: #c82333;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #e9ecef;
  }

  input {
    width: 20px;
    height: 20px;
    cursor: pointer;
  }

  div {
    flex: 1;
  }

  small {
    color: #666;
    display: block;
    margin-top: 0.25rem;
  }
`;

const AgentConfigPage = () => {
  const [aiConfig, setAiConfig] = useState({
    apiKey: '',
    baseUrl: 'https://api.deepseek.com/v1',
    model: 'deepseek-chat',
    timeout: 30
  });

  const [quickReplies, setQuickReplies] = useState([
    { id: '1', text: '算力', intent: 'product_inquiry', enabled: true },
    { id: '2', text: '智能体', intent: 'product_inquiry', enabled: true },
    { id: '3', text: '呼叫中心', intent: 'product_inquiry', enabled: true },
    { id: '4', text: '语音能力', intent: 'product_inquiry', enabled: true },
    { id: '5', text: '价格咨询', intent: 'price_inquiry', enabled: true }
  ]);

  const [serviceHours, setServiceHours] = useState({
    human: { start: 9, end: 18 },
    ai: { start: 0, end: 23 }
  });

  const [chatDisplaySettings, setChatDisplaySettings] = useState({
    showWelcomeMessage: false,
    showIntentInfo: false,
    showServiceStatus: true
  });

  const [storageSettings, setStorageSettings] = useState({
    retentionDays: 30
  });

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAllConfig();
  }, []);

  const fetchAllConfig = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };

      // 获取 AI 配置
      const aiRes = await axios.get('/api/admin/ai-config', { headers });
      if (aiRes.data.success) {
        setAiConfig({
          apiKey: aiRes.data.data.apiKey || '',
          baseUrl: aiRes.data.data.baseUrl || 'https://api.deepseek.com/v1',
          model: aiRes.data.data.model || 'deepseek-chat',
          timeout: aiRes.data.data.timeout || 30
        });
      }

      // 获取服务时段
      const hoursRes = await axios.get('/api/admin/service-hours', { headers });
      if (hoursRes.data.success) {
        setServiceHours(hoursRes.data.data);
      }

      // 获取显示设置
      const displayRes = await axios.get('/api/admin/chat-display-settings', { headers });
      if (displayRes.data.success) {
        setChatDisplaySettings(displayRes.data.data);
      }

      // 获取存储设置
      const storageRes = await axios.get('/api/admin/storage-settings', { headers });
      if (storageRes.data.success) {
        setStorageSettings(storageRes.data.data);
      }

      // 获取快捷回复
      const repliesRes = await axios.get('/api/admin/quick-replies', { headers });
      if (repliesRes.data.success && repliesRes.data.data.length > 0) {
        setQuickReplies(repliesRes.data.data);
      }
    } catch (error) {
      console.error('获取配置失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAIConfig = async () => {
    setLoading(true);
    setStatus(null);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.put('/api/admin/ai-config',
        { apiKey: aiConfig.apiKey, baseUrl: aiConfig.baseUrl, model: aiConfig.model },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setStatus({ type: 'success', message: 'API配置保存成功！' });
      } else {
        setStatus({ type: 'error', message: response.data.message || '保存失败' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: error.response?.data?.message || '保存失败' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveServiceHours = async () => {
    setLoading(true);
    setStatus(null);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.put('/api/admin/service-hours',
        serviceHours,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setStatus({ type: 'success', message: '服务时段保存成功！' });
      } else {
        setStatus({ type: 'error', message: response.data.message || '保存失败' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: error.response?.data?.message || '保存失败' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChatDisplaySettings = async () => {
    setLoading(true);
    setStatus(null);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.put('/api/admin/chat-display-settings',
        chatDisplaySettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setStatus({ type: 'success', message: '显示设置保存成功！' });
      } else {
        setStatus({ type: 'error', message: response.data.message || '保存失败' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: error.response?.data?.message || '保存失败' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStorageSettings = async () => {
    setLoading(true);
    setStatus(null);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.put('/api/admin/storage-settings',
        storageSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setStatus({ type: 'success', message: '存储设置保存成功！' });
      } else {
        setStatus({ type: 'error', message: response.data.message || '保存失败' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: error.response?.data?.message || '保存失败' });
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!aiConfig.apiKey) {
      setStatus({ type: 'error', message: '请先输入 API Key' });
      return;
    }

    setLoading(true);
    setStatus(null);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post('/api/admin/settings/test-ai',
        { apiKey: aiConfig.apiKey, baseUrl: aiConfig.baseUrl, model: aiConfig.model },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setStatus({ type: 'success', message: `连接测试成功！响应时间: ${response.data.responseTime}ms` });
      } else {
        setStatus({ type: 'error', message: response.data.message || '连接测试失败' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: error.response?.data?.message || '连接测试失败' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuickReply = () => {
    const newReply = {
      id: Date.now().toString(),
      text: '',
      intent: 'product_inquiry',
      enabled: true
    };
    setQuickReplies([...quickReplies, newReply]);
  };

  const handleRemoveQuickReply = (id) => {
    setQuickReplies(quickReplies.filter(reply => reply.id !== id));
  };

  const handleQuickReplyChange = (id, field, value) => {
    setQuickReplies(quickReplies.map(reply =>
      reply.id === id ? { ...reply, [field]: value } : reply
    ));
  };

  const handleSaveQuickReplies = async () => {
    setLoading(true);
    setStatus(null);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.put('/api/admin/quick-replies',
        { quickReplies },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setStatus({ type: 'success', message: '快捷回复保存成功！' });
      } else {
        setStatus({ type: 'error', message: response.data.message || '保存失败' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: error.response?.data?.message || '保存失败' });
    } finally {
      setLoading(false);
    }
  };

  const isConfigured = aiConfig.apiKey && aiConfig.apiKey.length > 0;

  return (
    <AdminLayout title="智能体配置">
      <Container>
        <Header>
          <Title>智能体配置</Title>
        </Header>

        {status && <Alert type={status.type}>{status.message}</Alert>}

        {/* API 连接配置 */}
        <Section>
          <SectionTitle>API 连接配置</SectionTitle>
          <FormGroup>
            <Label htmlFor="apiKey">DeepSeek API Key *</Label>
            <Input
              type="password"
              id="apiKey"
              value={aiConfig.apiKey}
              onChange={(e) => setAiConfig({ ...aiConfig, apiKey: e.target.value })}
              placeholder="请输入 DeepSeek API Key"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="baseUrl">API Base URL</Label>
            <Input
              type="text"
              id="baseUrl"
              value={aiConfig.baseUrl}
              onChange={(e) => setAiConfig({ ...aiConfig, baseUrl: e.target.value })}
              placeholder="https://api.deepseek.com/v1"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="model">模型</Label>
            <Select
              id="model"
              value={aiConfig.model}
              onChange={(e) => setAiConfig({ ...aiConfig, model: e.target.value })}
            >
              <option value="deepseek-chat">DeepSeek Chat</option>
              <option value="deepseek-coder">DeepSeek Coder</option>
              <option value="deepseek-chat-beta">DeepSeek Chat Beta</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="timeout">超时时间 (秒)</Label>
            <Input
              type="number"
              id="timeout"
              value={aiConfig.timeout}
              onChange={(e) => setAiConfig({ ...aiConfig, timeout: parseInt(e.target.value) || 30 })}
              min="5"
              max="120"
            />
          </FormGroup>

          <ButtonGroup>
            <Button variant="primary" onClick={handleSaveAIConfig} disabled={loading}>
              💾 保存配置
            </Button>
            <Button variant="info" onClick={handleTestConnection} disabled={loading}>
              🔗 测试连接
            </Button>
          </ButtonGroup>
        </Section>

        {/* 配置状态卡片 */}
        <ConfigCard>
          <ConfigCardTitle>当前配置状态</ConfigCardTitle>
          <ConfigCardValue>
            <StatusBadge active={isConfigured}>
              {isConfigured ? '✅ 已配置' : '⚠️ 未配置'}
            </StatusBadge>
          </ConfigCardValue>
          <div style={{ marginTop: '1rem', fontSize: '0.875rem', opacity: 0.9 }}>
            <div>模型: {aiConfig.model}</div>
            <div>超时: {aiConfig.timeout}秒</div>
            <div>API端点: {aiConfig.baseUrl}</div>
          </div>
        </ConfigCard>

        {/* 服务时段设置 */}
        <Section>
          <SectionTitle>服务时段设置</SectionTitle>
          <FormGroup>
            <Label>人工服务时段</Label>
            <TimeInputGroup>
              <TimeInputWrapper>
                <Label htmlFor="humanStart">开始时间</Label>
                <Select
                  id="humanStart"
                  value={serviceHours.human.start}
                  onChange={(e) => setServiceHours({ 
                    ...serviceHours, 
                    human: { ...serviceHours.human, start: parseInt(e.target.value) }
                  })}
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                  ))}
                </Select>
              </TimeInputWrapper>
              <TimeInputWrapper>
                <Label htmlFor="humanEnd">结束时间</Label>
                <Select
                  id="humanEnd"
                  value={serviceHours.human.end}
                  onChange={(e) => setServiceHours({ 
                    ...serviceHours, 
                    human: { ...serviceHours.human, end: parseInt(e.target.value) }
                  })}
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{i.toString().padStart(2, '0')}:59</option>
                  ))}
                </Select>
              </TimeInputWrapper>
            </TimeInputGroup>
          </FormGroup>

          <FormGroup>
            <Label>AI服务时段</Label>
            <TimeInputGroup>
              <TimeInputWrapper>
                <Label htmlFor="aiStart">开始时间</Label>
                <Select
                  id="aiStart"
                  value={serviceHours.ai.start}
                  onChange={(e) => setServiceHours({ 
                    ...serviceHours, 
                    ai: { ...serviceHours.ai, start: parseInt(e.target.value) }
                  })}
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                  ))}
                </Select>
              </TimeInputWrapper>
              <TimeInputWrapper>
                <Label htmlFor="aiEnd">结束时间</Label>
                <Select
                  id="aiEnd"
                  value={serviceHours.ai.end}
                  onChange={(e) => setServiceHours({ 
                    ...serviceHours, 
                    ai: { ...serviceHours.ai, end: parseInt(e.target.value) }
                  })}
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{i.toString().padStart(2, '0')}:59</option>
                  ))}
                </Select>
              </TimeInputWrapper>
            </TimeInputGroup>
          </FormGroup>

          <ButtonGroup>
            <Button variant="primary" onClick={handleSaveServiceHours} disabled={loading}>
              💾 保存服务时段
            </Button>
          </ButtonGroup>
        </Section>

        {/* 对话框显示设置 */}
        <Section>
          <SectionTitle>对话框显示设置</SectionTitle>
          <div style={{ marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
            控制智能客服对话框中的显示内容
          </div>

          <CheckboxLabel>
            <input
              type="checkbox"
              checked={chatDisplaySettings.showWelcomeMessage}
              onChange={(e) => setChatDisplaySettings({ 
                ...chatDisplaySettings, 
                showWelcomeMessage: e.target.checked 
              })}
            />
            <div>
              <strong>显示欢迎消息</strong>
              <small>开启后，客服对话框打开时会显示欢迎语和功能介绍</small>
            </div>
          </CheckboxLabel>

          <CheckboxLabel>
            <input
              type="checkbox"
              checked={chatDisplaySettings.showIntentInfo}
              onChange={(e) => setChatDisplaySettings({ 
                ...chatDisplaySettings, 
                showIntentInfo: e.target.checked 
              })}
            />
            <div>
              <strong>显示意图信息</strong>
              <small>开启后，客服会自动识别用户意图并在消息下方显示意图标签</small>
            </div>
          </CheckboxLabel>

          <CheckboxLabel>
            <input
              type="checkbox"
              checked={chatDisplaySettings.showServiceStatus}
              onChange={(e) => setChatDisplaySettings({ 
                ...chatDisplaySettings, 
                showServiceStatus: e.target.checked 
              })}
            />
            <div>
              <strong>显示服务时间</strong>
              <small>开启后，客服对话框上方会显示服务状态信息</small>
            </div>
          </CheckboxLabel>

          <ButtonGroup>
            <Button variant="primary" onClick={handleSaveChatDisplaySettings} disabled={loading}>
              💾 保存显示设置
            </Button>
          </ButtonGroup>
        </Section>

        {/* 存储设置 */}
        <Section>
          <SectionTitle>存储设置</SectionTitle>
          <FormGroup>
            <Label htmlFor="retentionDays">服务记录存储时间 (天)</Label>
            <Input
              type="number"
              id="retentionDays"
              value={storageSettings.retentionDays}
              onChange={(e) => setStorageSettings({ 
                ...storageSettings, 
                retentionDays: parseInt(e.target.value) || 30 
              })}
              min="1"
              max="365"
              placeholder="输入存储天数"
            />
            <small style={{ color: '#666', marginTop: '0.5rem', display: 'block' }}>
              超过存储时间的服务记录将被自动清理
            </small>
          </FormGroup>

          <ButtonGroup>
            <Button variant="primary" onClick={handleSaveStorageSettings} disabled={loading}>
              💾 保存存储设置
            </Button>
          </ButtonGroup>
        </Section>

        {/* 快捷回复配置 */}
        <Section>
          <SectionTitle>快捷回复配置</SectionTitle>
          <div style={{ marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
            设置客服聊天窗口中的快捷回复按钮，用户可以直接点击快速发起咨询
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <Label>快捷回复列表</Label>
            {quickReplies.map((reply) => (
              <QuickReplyCard key={reply.id}>
                <QuickReplyInput
                  type="text"
                  value={reply.text}
                  onChange={(e) => handleQuickReplyChange(reply.id, 'text', e.target.value)}
                  placeholder="快捷文本，如：算力"
                />
                <QuickReplySelect
                  value={reply.intent}
                  onChange={(e) => handleQuickReplyChange(reply.id, 'intent', e.target.value)}
                >
                  <option value="product_inquiry">产品咨询</option>
                  <option value="price_inquiry">价格咨询</option>
                  <option value="technical_support">技术支持</option>
                  <option value="solution_inquiry">解决方案咨询</option>
                  <option value="contact_inquiry">联系咨询</option>
                  <option value="company_info">公司信息</option>
                </QuickReplySelect>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={reply.enabled}
                    onChange={(e) => handleQuickReplyChange(reply.id, 'enabled', e.target.checked)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.9rem' }}>启用</span>
                </div>
                <DeleteButton onClick={() => handleRemoveQuickReply(reply.id)}>
                  删除
                </DeleteButton>
              </QuickReplyCard>
            ))}
          </div>
          <ButtonGroup>
            <Button variant="primary" onClick={handleAddQuickReply}>
              + 添加快捷回复
            </Button>
            <Button variant="primary" onClick={handleSaveQuickReplies} disabled={loading}>
              💾 保存快捷回复
            </Button>
          </ButtonGroup>
        </Section>
      </Container>
    </AdminLayout>
  );
};

export default AgentConfigPage;
